/**
 * Voice notification utility using Web Speech API.
 * Speaks Arabic messages in a male voice when available.
 */

let arabicVoice = null;
let voicesLoaded = false;

const pickArabicVoice = () => {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  // Prefer Arabic male voices
  const arabic = voices.filter((v) => /^ar/i.test(v.lang));
  const male = arabic.find(
    (v) =>
      /male|hamed|maged|hamza|abdullah|ahmed|naayf|saleh/i.test(v.name)
  );
  return male || arabic[0] || voices[0] || null;
};

const ensureVoice = () =>
  new Promise((resolve) => {
    if (!("speechSynthesis" in window)) return resolve(null);
    if (voicesLoaded && arabicVoice) return resolve(arabicVoice);
    const tryPick = () => {
      arabicVoice = pickArabicVoice();
      voicesLoaded = true;
      resolve(arabicVoice);
    };
    if (window.speechSynthesis.getVoices().length > 0) {
      tryPick();
    } else {
      window.speechSynthesis.onvoiceschanged = tryPick;
      // fallback timeout
      setTimeout(tryPick, 500);
    }
  });

export const speak = async (text) => {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  try {
    const voice = await ensureVoice();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "ar-SA";
    if (voice) utter.voice = voice;
    utter.rate = 0.95;
    utter.pitch = 0.85; // slightly lower for male tone
    utter.volume = 1;
    // Cancel any in-flight speech to avoid overlap
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch (e) {
    /* silent */
  }
};

/** Short notification beep using Web Audio API (works on all browsers). */
export const beep = (frequency = 880, durationMs = 180) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
  } catch (e) {
    /* silent */
  }
};

/** Continuous air-raid siren — plays the real MP3 the admin uploaded
 *  (/sounds/order-alert.mp3) for `durationMs`. Falls back to a synthesised
 *  oscillator sweep if the audio file is unavailable so we never go silent.
 *  Returns a `stop()` function so the alarm can be silenced instantly. */
let _activeAudioContext = null;
let _activeAudioNodes = [];
let _activeHtmlAudio = null;
const _stopAudio = () => {
  try {
    if (_activeHtmlAudio) {
      _activeHtmlAudio.pause();
      _activeHtmlAudio.currentTime = 0;
      _activeHtmlAudio = null;
    }
  } catch (e) { /* silent */ }
  try {
    _activeAudioNodes.forEach((n) => {
      try { n.stop && n.stop(0); } catch (e) { /* silent */ }
      try { n.disconnect && n.disconnect(); } catch (e) { /* silent */ }
    });
  } catch (e) { /* silent */ }
  _activeAudioNodes = [];
  try { _activeAudioContext && _activeAudioContext.close(); } catch (e) { /* silent */ }
  _activeAudioContext = null;
};
const _playMp3Siren = (durationMs) => {
  // Try the admin-uploaded siren MP3 first. Returns true if playback
  // started successfully so the caller can skip the oscillator fallback.
  try {
    const audio = new Audio("/sounds/order-alert.mp3");
    audio.preload = "auto";
    audio.volume = 1.0;
    audio.loop = durationMs > 10_500; // file is ~10s; loop only if longer
    _activeHtmlAudio = audio;
    audio.play().then(() => {
      // Hard-stop after the requested duration in case the file is
      // longer than expected (or looping).
      setTimeout(() => {
        if (_activeHtmlAudio === audio) {
          try { audio.pause(); audio.currentTime = 0; } catch (e) { /* silent */ }
          _activeHtmlAudio = null;
        }
      }, durationMs);
    }).catch(() => {
      // Autoplay blocked or file missing → fall back below.
      if (_activeHtmlAudio === audio) _activeHtmlAudio = null;
      _oscillatorSiren(durationMs);
    });
    return true;
  } catch (e) {
    return false;
  }
};
const _oscillatorSiren = (durationMs = 10000) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    _activeAudioContext = ctx;
    const now = ctx.currentTime;
    const dur = durationMs / 1000;

    // ── Primary siren oscillator (square wave — harsh, alarm-like) ──
    const osc1 = ctx.createOscillator();
    osc1.type = "sawtooth"; // closest to real siren timbre
    // Sweep 380 ↔ 1100 Hz with a 1.6-second period (typical air-raid pattern)
    const PERIOD = 1.6;
    const LOW = 380;
    const HIGH = 1100;
    let t = now;
    osc1.frequency.setValueAtTime(LOW, t);
    while (t < now + dur) {
      t += PERIOD / 2;
      osc1.frequency.linearRampToValueAtTime(HIGH, t);
      t += PERIOD / 2;
      osc1.frequency.linearRampToValueAtTime(LOW, t);
    }

    // ── Second harmonic oscillator (sub-octave fattening) ──
    const osc2 = ctx.createOscillator();
    osc2.type = "square";
    t = now;
    osc2.frequency.setValueAtTime(LOW / 2, t);
    while (t < now + dur) {
      t += PERIOD / 2;
      osc2.frequency.linearRampToValueAtTime(HIGH / 2, t);
      t += PERIOD / 2;
      osc2.frequency.linearRampToValueAtTime(LOW / 2, t);
    }

    // ── Master gain — loud, with quick attack & sustained level ──
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(0.85, now + 0.05);
    // Mild tremolo: dip the level every period for extra urgency.
    const dipPeriod = 0.4;
    let dt = now + 0.05;
    while (dt < now + dur - 0.2) {
      gain.gain.setValueAtTime(0.85, dt);
      gain.gain.linearRampToValueAtTime(0.55, dt + dipPeriod / 2);
      gain.gain.linearRampToValueAtTime(0.85, dt + dipPeriod);
      dt += dipPeriod;
    }
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);
    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + dur);
    osc2.stop(now + dur);
    _activeAudioNodes.push(osc1, osc2, gain);
  } catch (e) {
    /* silent */
  }
};
/** Soft, calm chime — gentle three-tone bell for non-urgent notifications
 *  (new orders, new signups, etc.). Replaces the harsh air-raid siren when
 *  the admin asked for "صوت هادئ". Volume is intentionally low (~0.18) and
 *  the chime is a clean sine ascending triad: E5 → G5 → C6 with a soft
 *  exponential decay. Returns silently if no AudioContext is available. */
export const gentleChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [
      { freq: 659.26, start: 0.00 },  // E5
      { freq: 783.99, start: 0.18 },  // G5
      { freq: 1046.50, start: 0.36 }, // C6
    ];
    tones.forEach(({ freq, start }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + 0.7);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + 0.75);
    });
    // Close ctx after the chime finishes
    setTimeout(() => { try { ctx.close(); } catch (e) { /* silent */ } }, 1500);
  } catch (e) { /* silent */ }
};

export const airRaidSiren = (durationMs = 10000) => {
  _stopAudio();
  _playMp3Siren(durationMs);
  return _stopAudio;
};

/** Strong alarm-style beep — kept for backward compatibility. Wraps the
 *  air-raid siren for a short 1.6-second pulse. */
export const alarmBeep = () => airRaidSiren(1600);

export const notify = async (text) => {
  beep();
  setTimeout(() => speak(text), 220);
};

/** Emergency-style notify: loud siren that repeats for ~10 seconds + speaks
 *  the message a few times. Returns a `stop()` function so a UI button can
 *  silence the alarm immediately. Calling alarmNotify again before the
 *  previous one ends will cancel the previous siren first. */
let _activeAlarmTimers = [];
let _activeAlarmStop = null;
const _clearActiveAlarm = () => {
  _activeAlarmTimers.forEach((t) => clearTimeout(t));
  _activeAlarmTimers = [];
  try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* silent */ }
  _stopAudio();  // kills the air-raid siren immediately
  _activeAlarmStop = null;
  if (_onAlarmStop) try { _onAlarmStop(); } catch (e) { /* silent */ }
};
export const stopAlarm = () => _clearActiveAlarm();
export const isAlarmActive = () => _activeAlarmStop != null;
export const onAlarmStart = (cb) => { _onAlarmStart = cb; };
export const onAlarmStop = (cb) => { _onAlarmStop = cb; };
let _onAlarmStart = null;
let _onAlarmStop = null;
export const alarmNotify = async (text, durationMs = 10000) => {
  _clearActiveAlarm();
  _activeAlarmStop = () => _clearActiveAlarm();
  if (_onAlarmStart) try { _onAlarmStart(text); } catch (e) { /* silent */ }
  // Single continuous air-raid siren for the FULL duration — a true
  // war-time alert, not a series of beeps. Speech is interleaved twice
  // for clarity. _stopAudio() (called from _clearActiveAlarm) kills
  // both the audio AND the speech synth.
  airRaidSiren(durationMs);
  setTimeout(() => speak(text), 350);
  // Re-speak halfway through so the admin doesn't miss the message.
  _activeAlarmTimers.push(setTimeout(() => speak(text), Math.floor(durationMs / 2)));
  // Auto-stop after duration so the floating button hides itself.
  _activeAlarmTimers.push(setTimeout(() => {
    _activeAlarmStop = null;
    if (_onAlarmStop) try { _onAlarmStop(); } catch (e) { /* silent */ }
  }, durationMs + 100));
};

/** Map of order-status keys → Arabic phrases for customer. */
export const customerStatusVoice = {
  confirmed: "تم تأكيد طلبك من الإدارة، جاري التحضير",
  assigned: "تم تعيين سائق لطلبك",
  accepted: "وافق السائق على توصيل طلبك",
  preparing: "جاري تجهيز طلبك",
  ready: "تم تجهيز طلبك",
  on_the_way: "طلبك في الطريق إليك",
  awaiting_payment_confirmation: "تم وصول طلبك بانتظار تأكيد الإدارة",
  delivered: "تم وصول طلبك، شكراً لتسوقك من جملتي",
  rejected: "تم إلغاء طلبك",
};

export const driverStatusVoice = {
  assigned: "وصلك طلب جديد",
};

export const adminVoice = {
  newOrder: "إجاكم طلب جديد! إجاكم طلب جديد!",
  paymentConfirmation: "طلب تأكيد استلام مبلغ جديد",
  newDriver: "سائق جديد سجّل في النظام",
  newMujahez: "مجهّز جديد سجّل في النظام",
  newWithdrawal: "طلب سحب أرباح جديد",
  newDebtPayment: "طلب تسديد دين جديد",
  newReturn: "طلب إرجاع جديد",
  newCashSubmission: "تسليم نقد من السائق",
};
